import type { Doc } from './richtext.js';

/**
 * Accès à l'API.
 *
 * Le domaine n'est jamais construit ici : le navigateur envoie déjà le bon
 * `Host`, et c'est lui qui désigne la base du client côté serveur. Une URL
 * relative suffit donc, et c'est plus sûr — il n'existe aucun endroit où
 * l'on pourrait se tromper de client.
 */

export type Espace = { id: string; slug: string; name: string; role: string };
export type Canal = {
  id: string; name: string; kind: string; topic: string;
  joined: boolean; notification: 'all' | 'mentions' | 'none';
  member_count: number; unread: number; to_handle: number; mentions: number;
};
export type Message = {
  id: string; channel_id: string; parent_id: string | null;
  author_id: string | null; display_name: string | null; avatar_color: string | null;
  body: Doc; created_at: string; edited_at: string | null; deleted?: boolean;
  reply_count?: number; last_reply_at?: string | null;
  reactions?: Array<{ emoji: string; count: number; me: boolean }>;
  /** Posé côté client tant que l'accusé n'est pas revenu. */
  en_cours?: boolean;
  client_token?: string;
};
export type Membre = {
  id: string; display_name: string; email: string; avatar_color: string; role: string;
};

export class ErreurApi extends Error {
  constructor(readonly statut: number, readonly code: string, message: string) {
    super(message);
  }
}

export function creerApi(jeton: () => string | null) {
  let espaceCourant: string | null = null;

  async function appel<T>(chemin: string, init: RequestInit = {}): Promise<T> {
    const t = jeton();
    const entetes: Record<string, string> = { ...(init.headers as Record<string, string>) };
    if (t) entetes.authorization = `Bearer ${t}`;
    if (espaceCourant) entetes['x-zephyr-workspace'] = espaceCourant;
    if (init.body && !entetes['content-type']) entetes['content-type'] = 'application/json';

    const r = await fetch(chemin, { ...init, headers: entetes });
    if (r.status === 204) return undefined as T;

    const type = r.headers.get('content-type') ?? '';
    if (!type.includes('application/json')) {
      if (!r.ok) throw new ErreurApi(r.status, 'reponse_inattendue', `Le serveur a répondu ${r.status}.`);
      return (await r.blob()) as T;
    }
    const corps = await r.json();
    if (!r.ok) {
      // Le message du serveur dit quoi faire : on le remonte tel quel
      // plutôt que de le remplacer par « une erreur est survenue ».
      throw new ErreurApi(r.status, corps.error ?? 'erreur', corps.message ?? `Erreur ${r.status}.`);
    }
    return corps as T;
  }

  return {
    definirEspace: (id: string | null) => { espaceCourant = id; },
    espaceCourant: () => espaceCourant,

    moi: () => appel<{ user: { id: string; display_name: string; email: string; avatar_color: string } }>('/v1/me'),
    espaces: () => appel<{ workspaces: Espace[] }>('/v1/workspaces'),
    creerEspace: (slug: string, name: string) =>
      appel<{ workspace: Espace }>('/v1/workspaces', {
        method: 'POST', body: JSON.stringify({ slug, name }),
      }),
    membres: (espace: string) => appel<{ members: Membre[] }>(`/v1/workspaces/${espace}/members`),

    canaux: () => appel<{ channels: Canal[] }>('/v1/channels'),
    creerCanal: (name: string, topic = '', kind: 'public' | 'private' = 'public') =>
      appel<{ channel: { id: string } }>('/v1/channels', {
        method: 'POST', body: JSON.stringify({ name, topic, kind }),
      }),
    rejoindre: (canal: string) =>
      appel<{ joined: boolean }>(`/v1/channels/${canal}/join`, { method: 'POST' }),
    marquerLu: (canal: string) =>
      appel<unknown>(`/v1/channels/${canal}/read`, { method: 'POST' }),
    reglerNotification: (canal: string, level: 'all' | 'mentions' | 'none') =>
      appel<unknown>(`/v1/channels/${canal}/notification`, {
        method: 'PUT', body: JSON.stringify({ level }),
      }),

    messages: (canal: string, options: { before?: string; thread?: string } = {}) => {
      const p = new URLSearchParams();
      if (options.before) p.set('before', options.before);
      if (options.thread) p.set('thread', options.thread);
      const q = p.toString();
      return appel<{ messages: Message[]; next_before: string | null }>(
        `/v1/channels/${canal}/messages${q ? '?' + q : ''}`,
      );
    },
    /** Repli quand la socket est coupée : l'envoi passe par HTTP. */
    envoyer: (canal: string, body: Doc, parent_id?: string) =>
      appel<{ message: Message }>(`/v1/channels/${canal}/messages`, {
        method: 'POST', body: JSON.stringify({ body, parent_id }),
      }),
    supprimer: (message: string) =>
      appel<void>(`/v1/messages/${message}`, { method: 'DELETE' }),
    reagir: (message: string, emoji: string) =>
      appel<{ reacted: boolean }>(
        `/v1/messages/${message}/reactions/${encodeURIComponent(emoji)}`, { method: 'PUT' },
      ),

    activite: (options: { unread_only?: boolean } = {}) =>
      appel<{ activity: unknown[]; unread: number; unread_mentions: number }>(
        `/v1/activity${options.unread_only ? '?unread_only=true' : ''}`,
      ),
    acquitter: (canal?: string) =>
      appel<{ marked: number }>('/v1/notifications/read', {
        method: 'POST',
        body: JSON.stringify(canal ? { channel_id: canal } : { all: true }),
      }),

    rechercher: (q: string) =>
      appel<{ results: Array<{ id: string; channel_name: string; display_name: string; extrait: string }> }>(
        `/v1/search?q=${encodeURIComponent(q)}`,
      ),

    fichiers: (canal: string) =>
      appel<{ attachments: Array<{ id: string; filename: string; bytes: number; state: string }> }>(
        `/v1/channels/${canal}/attachments`,
      ),
    deposer: (canal: string, fichier: File) =>
      appel<{ attachment: { id: string; filename: string } }>(
        `/v1/channels/${canal}/attachments`,
        {
          method: 'POST',
          body: fichier,
          headers: {
            'content-type': fichier.type || 'application/octet-stream',
            'x-zephyr-filename': fichier.name,
          },
        },
      ),
  };
}

export type Api = ReturnType<typeof creerApi>;
